import { Link } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, MapPin } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d23d8491-b3b1-4bd6-8f0e-4d1750125554/artifacts/uxopwla6_WhatsApp%20Image%202026-03-10%20at%204.20.57%20PM.jpeg";
const HEAD = "font-['Cabinet_Grotesk',_'Manrope',_sans-serif]";
const COMPANY = "Stay Point Private Limited";
const SUPPORT_EMAIL = "info@staypointindia.com";
const LAST_UPDATED = "June 2026";

const LegalLayout = ({ title, children, testid }) => (
  <div className="min-h-screen bg-white text-zinc-900 font-['Outfit',_'IBM_Plex_Sans',_sans-serif] antialiased" data-testid={testid}>
    {/* Top bar */}
    <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-zinc-100">
      <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="legal-home-link">
          <img src={LOGO_URL} alt="StayPoint" className="w-9 h-9 rounded-xl object-contain ring-1 ring-zinc-200" />
          <span className={`${HEAD} text-lg font-extrabold tracking-tight text-zinc-950`}>StayPoint</span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-600 hover:text-indigo-700 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </div>
    </header>

    {/* Content */}
    <main className="max-w-3xl mx-auto px-5 py-12 md:py-16">
      <h1 className={`${HEAD} text-3xl md:text-4xl font-extrabold tracking-tight text-zinc-950`}>{title}</h1>
      <p className="text-sm text-zinc-400 mt-2">Last updated: {LAST_UPDATED}</p>
      <div className="mt-8 space-y-7 text-[15px] leading-relaxed text-zinc-700">{children}</div>
    </main>

    {/* Footer */}
    <footer className="border-t border-zinc-100 bg-zinc-50">
      <div className="max-w-3xl mx-auto px-5 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-zinc-500">
        <p>© {new Date().getFullYear()} {COMPANY}. All rights reserved.</p>
        <div className="flex items-center gap-4">
          <Link to="/privacy" className="hover:text-indigo-700">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-indigo-700">Terms of Service</Link>
        </div>
      </div>
    </footer>
  </div>
);

const Section = ({ heading, children }) => (
  <section>
    <h2 className={`${HEAD} text-xl font-bold text-zinc-900 mb-2`}>{heading}</h2>
    <div className="space-y-2">{children}</div>
  </section>
);

export const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy" testid="privacy-policy-page">
    <p>
      {COMPANY} (“StayPoint”, “we”, “us”) provides a property-management platform for PG and hostel
      owners. This Privacy Policy explains what information we collect through StayPoint, how we use it,
      and the choices you have. By using StayPoint, you agree to the practices described here.
    </p>

    <Section heading="1. Information We Collect">
      <p>We collect information needed to operate the service for property owners and their staff:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li><strong>Account data:</strong> name, email, mobile number, role and organisation details of users who sign in.</li>
        <li><strong>Guest &amp; tenant records:</strong> guest name, mobile number, emergency contact, room/bed allotment, joining and leaving dates entered by the property owner.</li>
        <li><strong>Financial records:</strong> rent, deposits, electricity charges, payments, expenses and ledger entries recorded by the owner.</li>
        <li><strong>Enquiry data:</strong> details you submit through our “Request Access” or contact forms (name, phone, organisation, message).</li>
        <li><strong>Usage data:</strong> basic technical logs (such as access times) used to keep the service secure and reliable.</li>
      </ul>
    </Section>

    <Section heading="2. How We Use Information">
      <ul className="list-disc pl-5 space-y-1.5">
        <li>To provide and operate StayPoint — managing guests, rooms, billing, reports and accounting.</li>
        <li>To authenticate users and keep each organisation’s data isolated and secure.</li>
        <li>To respond to enquiries and provide customer support.</li>
        <li>To maintain, improve and troubleshoot the platform.</li>
      </ul>
    </Section>

    <Section heading="3. Data Ownership &amp; Controller">
      <p>
        Guest and financial records belong to the property owner (the “organisation”) that enters them.
        StayPoint processes this data on the owner’s behalf. Owners are responsible for obtaining any
        consent required from their guests for storing such records.
      </p>
    </Section>

    <Section heading="4. Sharing of Information">
      <p>
        We do <strong>not</strong> sell your data. Information is only accessible to authorised users within
        your own organisation. We may share data with trusted service providers (for example, secure cloud
        hosting) strictly to operate the platform, or where required by law.
      </p>
    </Section>

    <Section heading="5. Data Security">
      <p>
        We use reasonable technical and organisational measures to protect your data, including access
        controls and organisation-level data isolation so one company cannot see another’s records. No
        method of transmission or storage is 100% secure, but we work to safeguard your information.
      </p>
    </Section>

    <Section heading="6. Data Retention">
      <p>
        We retain organisation and guest records for as long as your account is active or as needed to
        provide the service. You may request deletion of your data by contacting us, subject to any legal
        or accounting retention requirements.
      </p>
    </Section>

    <Section heading="7. Your Rights">
      <p>
        You may request access to, correction of, or deletion of your personal information by contacting us
        at the email below. Owners can also edit or remove records directly within the platform.
      </p>
    </Section>

    <Section heading="8. Changes to This Policy">
      <p>
        We may update this Privacy Policy from time to time. Material changes will be reflected by updating
        the “Last updated” date above.
      </p>
    </Section>

    <Section heading="9. Contact Us">
      <p>For any questions about this Privacy Policy or your data, contact us:</p>
      <ul className="space-y-1.5 text-zinc-700">
        <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-indigo-600" /> <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-indigo-700">{SUPPORT_EMAIL}</a></li>
        <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-indigo-600" /> <a href="tel:+919904048600" className="hover:text-indigo-700">+91 9904048600</a></li>
        <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-indigo-600" /> {COMPANY}, India</li>
      </ul>
    </Section>
  </LegalLayout>
);

export const TermsOfService = () => (
  <LegalLayout title="Terms of Service" testid="terms-page">
    <p>
      These Terms of Service (“Terms”) govern your use of the StayPoint platform provided by {COMPANY}.
      By accessing or using StayPoint, you agree to these Terms.
    </p>

    <Section heading="1. Use of the Service">
      <p>
        StayPoint is a software platform for managing PGs, hostels and similar properties. You agree to use
        it only for lawful business purposes and to keep your login credentials confidential. You are
        responsible for all activity that occurs under your account.
      </p>
    </Section>

    <Section heading="2. Accounts &amp; Access">
      <p>
        Access is provided to approved organisations and the users they invite. You are responsible for the
        accuracy of the data you enter and for managing your own staff’s access within the platform.
      </p>
    </Section>

    <Section heading="3. Your Data">
      <p>
        You retain ownership of the guest and financial records you enter. You grant us the limited right to
        store and process this data solely to provide the service. Our handling of personal data is
        described in our <Link to="/privacy" className="text-indigo-700 hover:underline">Privacy Policy</Link>.
      </p>
    </Section>

    <Section heading="4. Acceptable Use">
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Do not attempt to access another organisation’s data or breach platform security.</li>
        <li>Do not upload unlawful content or use the service to violate any person’s rights.</li>
        <li>Do not disrupt, reverse-engineer, or misuse the platform.</li>
      </ul>
    </Section>

    <Section heading="5. Service Availability">
      <p>
        We work to keep StayPoint available and reliable, but the service is provided “as is” without
        warranties of uninterrupted or error-free operation. We may perform maintenance or update features
        from time to time.
      </p>
    </Section>

    <Section heading="6. Limitation of Liability">
      <p>
        To the maximum extent permitted by law, {COMPANY} shall not be liable for any indirect, incidental
        or consequential damages, or loss of data or profits, arising from your use of the platform. You are
        responsible for maintaining your own records and verifying financial figures.
      </p>
    </Section>

    <Section heading="7. Termination">
      <p>
        We may suspend or terminate access for violation of these Terms. You may stop using the service at
        any time; on request we will assist with reasonable data export or deletion.
      </p>
    </Section>

    <Section heading="8. Changes to These Terms">
      <p>
        We may revise these Terms from time to time. Continued use of StayPoint after changes constitutes
        acceptance of the updated Terms.
      </p>
    </Section>

    <Section heading="9. Contact">
      <p>Questions about these Terms? Reach us at:</p>
      <ul className="space-y-1.5 text-zinc-700">
        <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-indigo-600" /> <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-indigo-700">{SUPPORT_EMAIL}</a></li>
        <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-indigo-600" /> <a href="tel:+919904048600" className="hover:text-indigo-700">+91 9904048600</a></li>
      </ul>
    </Section>
  </LegalLayout>
);
